/**
 * lenz-search-expand-v2.js
 *
 * ⚠️ ALTO. ANTES DE TOCAR ESTE ARCHIVO LEER `SEARCH_SYSTEM.md` (raíz del repo).
 *
 *    Este sistema se ha ROTO 5 veces por "mejoras" bien intencionadas:
 *      - Bug 1: r100 → "no encontramos" (threshold count>2)
 *      - Bug 2: O6110 (SKU) → "no encontramos" (hasLiteral via /search/suggest)
 *      - Bug 3: refresco visible al cargar (auto-expand en page-load)
 *      - Bug 4: 663 dejó de expandir (threshold ≥3 muy estricto)
 *      - Bug 5: 800 → expansión OR perdía "Lente 800mm" que el nativo SÍ
 *               encontraba (la OR rompe el match parcial de Shopify)
 *
 *    Reglas no negociables:
 *      1. Solo intervenir queries puro-dígito 2-4 chars
 *      2. Page-load NUNCA redirige (solo decora)
 *      3. NUNCA usar /search/suggest para DECIDIR (solo para extraer tokens)
 *      4. Submit interceptor es el ÚNICO punto activo
 *      5. NATIVE-FIRST: si /search nativo ya muestra el término, NO expandir
 *         (la OR degrada el match parcial amplio del término suelto)
 *
 *    Test de regresión obligatorio antes de pushear cambios:
 *      node scripts/test-search-regression.mjs    (debe pasar 7/7)
 *
 * Mejora la búsqueda en /search SOLO para queries puramente numéricas
 * que son fragmentos parciales de SKUs/modelos. Ejemplo del caso real:
 *   "663" → encuentra "Trípode Weifeng 6663A" via OR fallback.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * REESCRITO 2026-05-18 — Lecciones acumuladas
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Bug 1 ("r100"): threshold count>2 fallaba con count==2 → expandía mal.
 *   Fix: introduje hasLiteral.
 *
 * Bug 2 ("O6110"): hasLiteral via /search/suggest fallaba para SKUs
 *   (los SKUs no aparecen en títulos, el suggest los matchea por scoring).
 *   Fix: dejé de confiar en /search/suggest para decidir; usé /search nativo.
 *
 * Bug 3 ("refresco"): la auto-expansion en page-load causaba redirect visible:
 *   usuario veía resultados parciales → luego flash → resultados expandidos.
 *   Fix: eliminar auto-expansion en page-load. Solo expandir al submit
 *   (antes de navegar) → 1 sola navegación sin refresco.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Diseño actual: muy conservador, intercepción solo en submit
 * ────────────────────────────────────────────────────────────────────────────
 *
 * 1. SUBMIT INTERCEPT (único punto de intervención):
 *    Cuando el usuario envía el form con una query PURO-DÍGITO de 2-4 chars,
 *    el handler:
 *      a. Hace fetch a /search/suggest para extraer tokens alternativos
 *         (productos que contienen Q como substring en sus títulos).
 *      b. Construye una URL `/search?q=Q OR T1 OR T2...&lenz_q=Q` con la
 *         expansión que CABE en el límite de 11 cláusulas de Shopify.
 *      c. Navega a esa URL DIRECTAMENTE — sin pasar por /search?q=Q intermedia.
 *      d. Si el fetch falla / no encuentra tokens, navega con la query
 *         original (mejor que romper con OR vacío).
 *
 *    Trade-off: añade 300-1000ms entre Enter y la nueva URL. Aceptable
 *    porque solo aplica a queries puro-dígito de 2-4 chars (caso minoritario).
 *
 * 2. DECORACIÓN POST-EXPANSIÓN:
 *    Cuando la URL ya trae lenz_q (porque pasamos por intercepción), restauramos
 *    el input y el header para mostrar la query original en lugar de la OR.
 *
 * 3. NO HAY AUTO-EXPANSION EN PAGE-LOAD:
 *    Si usuario llega a /search?q=663 por bookmark/link directo, mostramos lo
 *    que Shopify devolvió (1 resultado parcial) sin tocar. Mejor 1 resultado
 *    consistente que un refresco para mostrar más resultados.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Por qué tan conservador con el shape de query
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  - "r100", "O6110", "t7", "a6400", "6663a": queries con letras.
 *    Shopify nativo las matchea bien (SKU exacto + título). NO intervenir.
 *
 *  - "663": puro dígito CORTO (≤4 chars). Posible fragmento de SKU más largo
 *    como "6663A". Vale la pena expandir.
 *
 *  - "12345": puro dígito LARGO (≥5 chars). Probablemente SKU completo o
 *    código específico. Shopify lo encontrará si existe; expandir solo
 *    sumaría ruido. NO intervenir.
 *
 *  - Cualquier otra cosa (espacios, símbolos, acentos): no tocar.
 */

(function () {
  'use strict';

  // ─── CONFIGURACIÓN ──────────────────────────────────────────────────────

  // 36 chars usados por el predictive (theme.js _doPredictiveSearch) para
  // pedirle al endpoint /search/suggest la expansión completa.
  var CHARS_FULL = '0123456789abcdefghijklmnopqrstuvwxyz';

  // Dígitos solo. Para el fallback puro si /search/suggest no devuelve tokens.
  var CHARS_DIGITS = '0123456789';

  // Límite de cláusulas OR aceptadas por /search (empírico: 12+ devuelve 0).
  var MAX_OR_CLAUSES = 11;

  // Sólo intervenimos en queries de PURO DÍGITO y CORTAS (2-4 chars).
  // Más largo que eso es probable un SKU completo (lo deja Shopify nativo).
  var EXPANSION_MIN_LEN = 2;
  var EXPANSION_MAX_LEN = 4;

  // Timeout del fetch a /search/suggest (predictive, ~10KB → rápido).
  var SUGGEST_TIMEOUT_MS = 2500;

  // Timeout del fetch a /search NATIVO. Esta página es PESADA (~450KB con todo
  // el chrome del tema) y el primer fetch en frío puede tardar ~4s. Un timeout
  // corto causaría null → expansión equivocada (Bug 5). Por eso es más holgado.
  // Si igual expira, el fail-safe navega PLANO (ver decisión en el submit).
  var NATIVE_TIMEOUT_MS = 4000;

  // Filtros para tokens al parsear el HTML del predictive.
  var TOKEN_MIN_LEN = 2;
  var TOKEN_MAX_LEN = 24;

  // ─── HELPERS ────────────────────────────────────────────────────────────

  /**
   * Decide si una query merece intercepción en el submit. Heurística estricta:
   * solo queries puro-dígito de 2-4 chars. Las demás pasan directo a Shopify.
   */
  function isExpansionCandidate(q) {
    if (!q) return false;
    var trimmed = q.trim();
    if (trimmed.length < EXPANSION_MIN_LEN) return false;
    if (trimmed.length > EXPANSION_MAX_LEN) return false;
    return /^[0-9]+$/.test(trimmed);
  }

  function buildOrQuery(clauses) {
    return clauses.slice(0, MAX_OR_CLAUSES).join(' OR ');
  }

  function buildDigitOnlyExpansion(q) {
    var lower = q.toLowerCase().trim();
    var variants = CHARS_DIGITS.split('').map(function (c) { return c + lower; });
    return buildOrQuery([lower].concat(variants));
  }

  /**
   * Fetch a /search/suggest con expansión completa de 36 chars.
   * Extrae tokens alfanuméricos de los títulos de productos retornados
   * que CONTIENEN `origQ` como substring (= modelos alternativos plausibles).
   *
   * Resuelve a un array de tokens (sin la query original, ordenado por
   * frecuencia descendente) o null si el fetch falla.
   */
  function findAlternateTokens(origQ) {
    return new Promise(function (resolve) {
      var trimmed = origQ.toLowerCase().trim();

      var predictiveEl = document.querySelector('predictive-search[section-id]');
      var sectionId = predictiveEl && predictiveEl.getAttribute('section-id');
      if (!sectionId) return resolve(null);

      var variants = CHARS_FULL.split('').map(function (c) { return c + trimmed; });
      var fullExpanded = [trimmed].concat(variants).join(' OR ');

      var url = new URL('/search/suggest', window.location.origin);
      url.searchParams.set('q', fullExpanded);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('type', 'product');
      url.searchParams.set('resources[options][unavailable_products]', 'show');
      url.searchParams.set('resources[limit]', '15');
      url.searchParams.set('resources[limit_scope]', 'each');

      var ctl = new AbortController();
      var timeoutId = setTimeout(function () { ctl.abort(); }, SUGGEST_TIMEOUT_MS);

      fetch(url.toString(), { credentials: 'same-origin', signal: ctl.signal })
        .then(function (res) {
          clearTimeout(timeoutId);
          return res.ok ? res.text() : null;
        })
        .then(function (html) {
          if (!html) return resolve(null);
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var cards = doc.querySelectorAll('a[href*="/products/"]');
          if (!cards.length) return resolve(null);

          var tokens = Object.create(null);
          cards.forEach(function (card) {
            var text = (card.textContent || '').toLowerCase();
            var matches = text.match(/[a-z0-9]+/g) || [];
            matches.forEach(function (t) {
              if (t.length < TOKEN_MIN_LEN || t.length > TOKEN_MAX_LEN) return;
              if (t === trimmed) return;
              if (t.indexOf(trimmed) === -1) return;
              tokens[t] = (tokens[t] || 0) + 1;
            });
          });

          var sorted = Object.keys(tokens).sort(function (a, b) {
            return tokens[b] - tokens[a];
          });
          resolve(sorted.length ? sorted : null);
        })
        .catch(function () {
          clearTimeout(timeoutId);
          resolve(null);
        });
    });
  }

  /**
   * Fetch a /search?q=Q NATIVO (término suelto, sin OR). Sirve para DECIDIR
   * si la búsqueda nativa de Shopify YA resuelve la query — en cuyo caso NO
   * hay que expandir, porque la cláusula OR ROMPE el match parcial nativo.
   *
   * Por qué existe (caso real 2026-06, query "800"):
   *   - /search?q=800 (bare)        → 25 resultados, incluye "...rf-800mm..."
   *   - /search?q=800 OR 1800x      →  9 resultados, SIN el lente
   *   - /search?q=800 OR 800mm      →  7 resultados (incluye lente pero degrada)
   *   Shopify hace match parcial amplio con el término suelto, pero al envolver
   *   en OR pasa a matching estricto por cláusula y pierde "800"→"800mm".
   *   Conclusión: si el nativo ya muestra Q, usar el nativo tal cual.
   *
   * IMPORTANTE: usa /search NATIVO para decidir, NUNCA /search/suggest
   * (regla cardinal del sistema — ver SEARCH_SYSTEM.md, Bug 2.5).
   *
   * Resuelve a { titleMatch: bool, count: number } o null si el fetch falla.
   *   - titleMatch: algún producto del MAIN search tiene Q como substring en
   *     su handle (slug). El handle deriva del título, así que es señal fiable
   *     de que Shopify ya surfaceó el término — y evita falsos positivos del
   *     precio (ej. "S/ 1,800" NO debe contar como match de "800").
   *   - count: nº de productos únicos en el MAIN search.
   */
  function fetchNativeResults(origQ) {
    return new Promise(function (resolve) {
      var trimmed = origQ.toLowerCase().trim();

      var url = new URL('/search', window.location.origin);
      url.searchParams.set('q', trimmed);
      url.searchParams.set('type', 'product');

      var ctl = new AbortController();
      var timeoutId = setTimeout(function () { ctl.abort(); }, NATIVE_TIMEOUT_MS);

      fetch(url.toString(), { credentials: 'same-origin', signal: ctl.signal })
        .then(function (res) {
          clearTimeout(timeoutId);
          return res.ok ? res.text() : null;
        })
        .then(function (html) {
          if (!html) return resolve(null);
          var doc = new DOMParser().parseFromString(html, 'text/html');
          // Acotar al MAIN search: la página /search embebe también el
          // predictive drawer del header con los mismos productos. Sin acotar,
          // contaríamos duplicados. Fallback a doc completo si cambia el tema.
          var scope = doc.querySelector('.shopify-section--main-search') || doc;
          var links = scope.querySelectorAll('a[href*="/products/"]');

          var handles = Object.create(null);
          var titleMatch = false;
          links.forEach(function (a) {
            var href = a.getAttribute('href') || '';
            var m = href.match(/\/products\/([a-z0-9-]+)/);
            if (!m) return;
            var handle = m[1];
            handles[handle] = true;
            if (handle.indexOf(trimmed) !== -1) titleMatch = true;
          });

          resolve({ titleMatch: titleMatch, count: Object.keys(handles).length });
        })
        .catch(function () {
          clearTimeout(timeoutId);
          resolve(null);
        });
    });
  }

  // ─── DECORACIÓN POST-EXPANSIÓN ──────────────────────────────────────────

  /**
   * Cuando la URL tiene lenz_q (= pasamos por intercepción en submit),
   * restauramos la UI para mostrar la query original en lugar de la OR larga.
   *
   * Ejecuta SIN flicker porque la página /search ya viene con resultados
   * desde el server-side render (no hay redirect intermedio).
   */
  function decorateOriginalQuery(origQ) {
    document.querySelectorAll('form.main-search-form input[name="q"]').forEach(function (input) {
      input.value = origQ;
    });

    var headers = document.querySelectorAll(
      '.shopify-section--main-search h1, .shopify-section--main-search h2'
    );
    headers.forEach(function (h) {
      var text = (h.textContent || '').trim();
      var m = text.match(/^\s*(\d+)\s+(resultado|resultados)\b/i);
      if (m) {
        var count = parseInt(m[1], 10);
        var noun = count === 1 ? 'resultado' : 'resultados';
        h.textContent = count + ' ' + noun + ' para "' + origQ + '"';
      }
    });

    // Preservar lenz_q en los facet forms para mantener contexto al filtrar
    document.querySelectorAll('form#facet-form, form.facets-vertical').forEach(function (form) {
      if (form.querySelector('input[name="lenz_q"]')) return;
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'lenz_q';
      hidden.value = origQ;
      form.appendChild(hidden);
    });
  }

  // ─── SUBMIT INTERCEPT ───────────────────────────────────────────────────

  /**
   * Intercepta el submit del search form. Si la query es candidata a
   * expansión (puro-dígito 2-4 chars), bloquea la navegación, hace fetch
   * a /search/suggest, construye URL con OR, y navega a esa URL.
   *
   * Resultado neto para el usuario: 1 sola navegación (sin refresco).
   */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    var action = (form.getAttribute('action') || '').toLowerCase();
    // El form de búsqueda apunta a /search; aceptamos también "" (= página actual)
    if (!/(?:^|\/)search(?:\?|$|\/)/.test(action) && action !== '') return;

    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    var q = (input.value || '').trim();
    if (!isExpansionCandidate(q)) return;

    e.preventDefault();
    e.stopPropagation();

    function navigateWithQ(finalQ, isExpanded) {
      var url = new URL(form.getAttribute('action') || '/search', window.location.origin);
      var fd = new FormData(form);
      fd.set('q', finalQ);
      if (isExpanded) {
        fd.set('lenz_q', q);  // marca para decorar UI en la página de resultados
      }
      if (!fd.get('type')) fd.set('type', 'product');
      fd.forEach(function (value, key) {
        url.searchParams.set(key, String(value));
      });
      window.location.href = url.toString();
    }

    // Decisión NATIVE-FIRST (fail-safe a PLANO). Pedimos EN PARALELO:
    //   a) fetchNativeResults(q) → qué devuelve el /search nativo (bare).
    //   b) findAlternateTokens(q) → tokens alternativos del predictive.
    //
    // La expansión OR es la operación RIESGOSA (rompió "800"). Solo expandimos
    // con EVIDENCIA POSITIVA de que el nativo es pobre. Ante la duda → PLANO.
    //
    //   1. Nativo bueno (Q en algún handle)        → PLANO.        (caso "800")
    //   2. Nativo respondió POBRE + hay tokens      → EXPANDIR OR.  (caso "663")
    //   3. Nativo pobre sin tokens pero con algo    → PLANO.
    //   4. Nativo desconocido (timeout/fallo)       → PLANO (fail-safe).
    //      Un nativo lento suele implicar MUCHOS resultados (página pesada) ⇒
    //      plano es lo correcto. Un nativo pobre responde rápido (página chica),
    //      así que SÍ alcanza a disparar la expansión del caso 2.
    //   5. Nativo vacío (0 resultados) y sin tokens → digit-only (último recurso).
    Promise.all([fetchNativeResults(q), findAlternateTokens(q)]).then(function (res) {
      var native = res[0];
      var tokens = res[1];

      // 1. Nativo ya muestra el término → no tocar.
      if (native && native.titleMatch) {
        return navigateWithQ(q, false);
      }
      // 2. Nativo respondió y es pobre (sin match) → expandir si hay tokens.
      if (native && !native.titleMatch && tokens && tokens.length > 0) {
        var expanded = buildOrQuery([q.toLowerCase()].concat(tokens));
        return navigateWithQ(expanded, true);
      }
      // 3. Nativo pobre, sin tokens, pero trajo algún resultado → plano.
      if (native && native.count > 0) {
        return navigateWithQ(q, false);
      }
      // 4. Nativo desconocido (timeout/fallo) → fail-safe a plano. NO arriesgar
      //    la expansión: si el nativo era bueno (página pesada y por eso lenta),
      //    la OR lo habría roto.
      if (!native) {
        return navigateWithQ(q, false);
      }
      // 5. Nativo respondió 0 resultados y no hay tokens → digit-only.
      return navigateWithQ(buildDigitOnlyExpansion(q), true);
    });
  }, true);

  // ─── INIT: solo decoración, NUNCA redirect ──────────────────────────────

  function init() {
    var params = new URLSearchParams(window.location.search);
    var origQ = params.get('lenz_q');
    if (origQ) decorateOriginalQuery(origQ);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
